import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/node';

import {
  FrequentlyBoughtTogether,
  RelatedProducts
} from '@algolia/recommend-react';
import recommend from '@algolia/recommend';

import {
  createRecentlyViewed,
  getRecentlyViewed,
  getSLIWishedProducts as getProducts,
  getRelatedProducts,
  getAlsoBoughtWithProducts,
  getProduct as fetchProduct
} from '../api';

import { generateMeta, generateBreadcrumbSeoSchema } from '../../utils/meta';
import { Page, Content } from '../../theme/page';
import { trackProductView } from '../../utils/tracking';
import getCookie from '../../utils/getCookie';
import hasError from '../../utils/hasError';
import SCOPE_TYPES from '../../utils/sentryScopeTypes';
import getCanonicalUrl from '../../utils/getCanonicalUrl';
import { useSiteSpect } from '../../utils/context/sitespect-provider';

import { Skeleton, SkeletonWrapper } from '../../atoms/skeleton';
import BreadCrumbs from '../../molecules/breadcrumbs';
import ErrorPage from '../_error';
import ProductCardCarousel from '../../organs/product-card-carousel';
import ProductCardCarouselWrapper from '../../organs/product-algolia-card-carousel';
import ProductCarousel from '../../tissues/product-carousel';
import ProductDetails from '../../organs/pdp-details';

// Context
import { ProductProvider } from '../../utils/context/product-provider';

const Separator = styled.hr`
  background: ${(props) => props.theme.color.brand};
  border: none;
  margin: 3rem 0;
  width: 100%;
  height: 1px;
`;

const DetailsSkeletonWrapper = styled(SkeletonWrapper)`
  grid-area: product-information;
`;

const CarouselSkeletonWrapper = styled(SkeletonWrapper)`
display: flex;
  grid-area: product-carousel;

  ${Skeleton}::before {
    content: "";
    display: block;
    padding-bottom: 100%;
  }
`;

const BreadcrumbSkeletonWrapper = styled(SkeletonWrapper)`
  grid-area: breadcrumbs;

  ${Skeleton} {
    height: 1.5rem;
    margin-bottom: 3rem;
  }
`;

const DetailsSkeleton = () => (
  <DetailsSkeletonWrapper>
    {/* Product Header */}
    <Skeleton css={{ height: '12rem', marginBottom: '3rem' }} />

    {/* Product Information */}
    <Skeleton css={{ height: '35rem', marginBottom: '3rem' }} />

    {/* Product Options */}
    <Skeleton css={{ height: '30rem', marginBottom: '3rem' }} />

    {/* Product Options */}
    <Skeleton css={{ height: '7rem', marginBottom: '3rem' }} />
  </DetailsSkeletonWrapper>
);

const CarouselSkeleton = () => (
  <CarouselSkeletonWrapper>
    <Skeleton />
  </CarouselSkeletonWrapper>
);

const BreadcrumbSkeleton = () => (
  <BreadcrumbSkeletonWrapper>
    <Skeleton />
  </BreadcrumbSkeletonWrapper>
);

const Product = styled.div`
  display: grid;
  grid-gap: 3rem;
  grid-template-areas: 'breadcrumbs' 'product-carousel' 'product-information';
  grid-template-columns: minmax(0, 1fr);
  margin-bottom: 3rem;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    grid-gap: 5rem;
    grid-template-columns: 50%;
    grid-template-areas: 'breadcrumbs breadcrumbs' 'product-carousel product-information';
  }
`;

const getMetaTitle = (name, brand = '', category = '') => {
  let title = name;

  if (brand) {
    title += ` - ${brand}`;
    if (category) title += ` ${category}`;
  } else if (category) {
    title += ` - ${category}`;
  }

  title += ' | Maisonette';

  return title;
};

const getMetaDescription = (name, brand = '', category = '') => {
  let description = `Discover the ${name}`;

  if (brand) {
    description += ` from ${brand}.`;
    if (category) description += ` Shop ${category} and more from Maisonette's curated selection.`;
  } else if (category) {
    description += `. Shop ${category} and more from Maisonette's curated selection.`;
  }

  return description;
};

const getMetaDetails = (product) => {
  const { name, brand = '', breadcrumb_taxons = [] } = product;
  const category = breadcrumb_taxons[breadcrumb_taxons?.length - 1]?.name ?? null;

  return {
    title: getMetaTitle(name, brand, category),
    description: getMetaDescription(name, brand, category)
  };
};

const ProductDetailPage = (props) => {
  const [recentlyViewed, setRecentlyViewed] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState(false);
  const [alsoBoughtWith, setAlsoBoughtWith] = useState(false);
  const { algolia = false } = useSiteSpect();

  const router = useRouter();

  useEffect(() => {
    global.window.scrollTo(0, 0);
  }, []);

  const recommendClient = recommend(
    process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
    process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY
  );

  const setProductAsRecentlyViewed = async (variant_id) => {
    const session_id = getCookie('maisonette_session_token');

    await createRecentlyViewed({
      body: {
        recently_viewed: {
          session_id,
          variant_id
        }
      }
    });
  };

  const getRecentlyViewedProducts = () => {
    const session_id = getCookie('maisonette_session_token');

    getRecentlyViewed({ session_id })
      .then(async (res) => {
        if (hasError(res)) {
          setRecentlyViewed(false);
          return;
        }

        try {
          const ids = res.map((x) => x.variant_id);

          const sliProducts = await getProducts({ ids });

          const sortSliRes = () => {
            const products = sliProducts.results;
            const recent = res.map((x) => x.variant_id);

            return products.sort((a, b) => (
              recent.indexOf(+a.product_id_wishlist) - recent.indexOf(+b.product_id_wishlist)
            ));
          };

          setRecentlyViewed({
            ...sliProducts,
            results: sortSliRes()
              .filter((x) => +x.product_id_wishlist !== +props.product.master.id).slice(0, 20)
          });
        } catch (error) {
          setRecentlyViewed(false);
        }
      });
  };

  useEffect(() => {
    if (algolia) return;

    if (props.product && !hasError(props.product)) {
      setRelatedProducts(false);
      setAlsoBoughtWith(false);
      setProductAsRecentlyViewed(props.product.master.id);

      trackProductView({ product: props.product });

      getRelatedProducts({ sku: props.product?.master?.sku, count: 15 })
        .then((res) => {
          if (hasError(res)) return;
          setRelatedProducts(res);
        });

      getAlsoBoughtWithProducts({ sku: props.product?.master?.sku, count: 15 })
        .then((res) => {
          if (hasError(res)) return;
          setAlsoBoughtWith(res);
        });

      getRecentlyViewedProducts();
    }
  }, [props.product]);

  const getAvailability = () => {
    if (props.product?.variants?.length <= 0) return 'https://schema.org/OutOfStock';

    return props.product?.variants?.some(({ in_stock = false }) => in_stock)
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock';
  };

  const getPrice = () => {
    let price = 0;

    if (!props.product?.variants?.length) return price;

    for (let i = 0; i <= props.product.variants.length - 1; i++) {
      const variant = props.product.variants[i];
      if (variant.price) {
        price = variant.price;
        break;
      }
    }

    if (price) {
      const [whole, fraction] = price.toString().split('.');
      if (+fraction === 0) return whole;
      return price;
    }

    return price;
  };

  const getProductType = () => {
    const { classifications } = props.product;
    const [productType] = classifications.filter((c) => {
      const prettyName = c.taxon?.pretty_name;
      return prettyName.split('->')?.[0]?.trim() === 'Product Type';
    });
    return productType?.taxon?.name;
  };

  if (router?.isFallback) {
    return (
      <Page id="maincontent">
        <Content>
          <Product>
            <BreadcrumbSkeleton />
            <CarouselSkeleton />
            <DetailsSkeleton />
          </Product>
        </Content>
      </Page>
    );
  }

  if (!props.product || hasError(props.product)) {
    return <ErrorPage statusCode={404} />;
  }

  const hasImages = props.product?.master && props.product?.master?.images;

  const productMeta = getMetaDetails(props.product);

  const meta = {
    key: 'product',
    title: productMeta.title,
    description: productMeta.description,
    og: {
      description: productMeta.description,
      image: props.product.master.images.length > 0
        ? props.product.master.images[0]?.large_url ?? null
        : null,
      title: productMeta.title,
      url: `${process.env.NEXT_PUBLIC_CLIENT_HOST}${router?.asPath}`
    },
    twitter: {
      description: productMeta.description,
      image: props.product.master.images.length > 0
        ? props.product.master.images[0]?.large_url ?? null
        : null,
      title: productMeta.title
    }
  };

  return (
    <ProductProvider>
      <div key={props.product.id}>
        <Head>
          {generateMeta(meta)}

          <link key="canonical" rel="canonical" href={getCanonicalUrl(router)} />

          {generateBreadcrumbSeoSchema(props)}

          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: `
                {
                  "@context": "https://schema.org/", 
                  "@type": "Product", 
                  "name": "${props.product.name}",
                  "image": "${props?.product?.master?.images[0]?.large_url}",
                  "description": "${props?.product?.description}",
                  "brand": "${props?.product?.brand}",
                  "sku": "${props?.product?.master?.sku}",
                  "offers": {
                    "@type": "Offer",
                    "url": "${process.env.NEXT_PUBLIC_CLIENT_HOST}/product/${props?.product?.slug}",
                    "priceCurrency": "USD",
                    "price": "${getPrice(props?.product?.variants?.[0]?.price ?? props?.product?.master?.price)}",
                    "availability": "${getAvailability()}",
                    "itemCondition": "https://schema.org/NewCondition"
                  }
                }
              `
            }}
          />
        </Head>

        <Page id="maincontent">
          <Content>
            <Product>
              <BreadCrumbs breadcrumbs={props.product?.breadcrumb_taxons || []} />

              {
                hasImages && (
                  <ProductCarousel
                    images={props.product?.master?.images}
                    name={props.product?.master?.name}
                    productType={getProductType()}
                  />
                )
              }

              <ProductDetails product={props.product} />
            </Product>

            {
              (
                alsoBoughtWith?.results?.length > 0
                && relatedProducts?.results?.length > 0
              ) ? <Separator /> : null
            }

            {
              (alsoBoughtWith && !algolia) && (
                <ProductCardCarousel
                  title="Also Bought With"
                  products={alsoBoughtWith}
                  id="also-bought-with"
                  trackFor="Also Bought"
                  isInfinite
                />
              )
            }

            {
              algolia && (
                <FrequentlyBoughtTogether
                  recommendClient={recommendClient}
                  indexName={process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX}
                  objectIDs={[`${props.product?.master?.id}`]}
                  // https://www.algolia.com/doc/ui-libraries/recommend/api-reference/recommend-react/FrequentlyBoughtTogether/#param-fallbackcomponent
                  fallbackComponent={() => (
                    <ProductCardCarouselWrapper
                      brand={props.product?.brand}
                      title="Also Bought With"
                      id="also-bought-with"
                      trackFor="Also Bought"
                      productName={props.product?.name}
                      productBreadcrumbs={props.product?.breadcrumb_taxons}
                      productID={props.product?.id}
                    />
                  )}
                  view={({ items }) => (
                    <ProductCardCarousel
                      title="Also Bought With"
                      products={items}
                      id="also-bought-with"
                      trackFor="Also Bought"
                      isInfinite
                    />
                  )}
                  // eslint-disable-next-line react/no-children-prop
                  children={
                    ({ View, Fallback, recommendations }) => (
                      recommendations.length
                        ? <View />
                        : <Fallback />
                    )
                  }
                />
              )
            }

            {
              (alsoBoughtWith?.results?.length > 0
                && relatedProducts?.results?.length > 0
                && !algolia) ? <Separator /> : null
            }

            {
              (relatedProducts && !algolia) && (
                <ProductCardCarousel
                  title="Related Products"
                  products={relatedProducts}
                  id="related-products"
                  trackFor="Related Products"
                  isInfinite
                />
              )
            }

            {
              algolia && (
                <RelatedProducts
                  recommendClient={recommendClient}
                  indexName={process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX}
                  objectIDs={[`${props.product?.master?.id}`]}
                  fallbackComponent={() => (
                    <ProductCardCarouselWrapper
                      brand={props.product?.brand}
                      title="Related Products"
                      id="related-products"
                      trackFor="Related Products"
                      productName={props.product?.name}
                      productBreadcrumbs={props.product?.breadcrumb_taxons}
                      productID={props.product?.id}
                    />
                  )}
                  view={({ items }) => (
                    <ProductCardCarousel
                      title="Related Products"
                      products={items}
                      id="related-products"
                      trackFor="Related Products"
                      isInfinite
                    />
                  )}
                  // eslint-disable-next-line react/no-children-prop
                  children={
                    ({ View, Fallback, recommendations }) => (
                      recommendations.length
                        ? <View />
                        : <Fallback />
                    )
                  }
                />
              )
            }

            {
              (
                (relatedProducts?.results?.length > 0
                && recentlyViewed?.results?.length > 0
                && !algolia)
              ) ? <Separator /> : null
            }

            {
              recentlyViewed && (
                <ProductCardCarousel
                  title="Recently Viewed"
                  products={recentlyViewed}
                  id="recently-viewed"
                />
              )
            }
          </Content>
        </Page>
      </div>
    </ProductProvider>
  );
};

export async function getServerSideProps({ params }) {
  const { slug } = params;
  const getProduct = (id) => fetchProduct({
    base: process.env.SOLIDUS_HOST,
    uri: `/api/products/${id}`,
    scopes: [SCOPE_TYPES.SERVICES.SOLIDUS],
    id
  });

  // if there was an error fetching product
  // - return null for product,
  // - revalidation will try to fetch again on next attempt
  const product = await getProduct(slug)
    .catch((error) => {
      Sentry.withScope((scope) => {
        scope.setContext('slug', slug);
        scope.setFingerprint('/pages/product/[slug]::getServerSideProps');
        Sentry.captureException(error);
      });

      return null;
    });
  if (product && hasError(product)) return { notFound: true };

  return {
    props: { product }
  };
}

ProductDetailPage.defaultProps = {
  product: null
};

ProductDetailPage.propTypes = {
  product: PropTypes.object
};

export default ProductDetailPage;
